import { useEffect, useState, useCallback } from 'react';

/**
 * Custom hook for Google reCAPTCHA Enterprise
 * @returns {Object} - { isReady, executeRecaptcha }
 */
export const useRecaptchaEnterprise = () => {
  const [isReady, setIsReady] = useState(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);

  const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY || '';
  const recaptchaEnabled = import.meta.env.VITE_RECAPTCHA_ENABLED === 'Y';

  useEffect(() => {
    if (!recaptchaEnabled || !recaptchaSiteKey) {
      setIsReady(true);
      return;
    }

    // Check if script already exists
    const existingScript = document.querySelector(
      'script[src*="recaptcha/enterprise.js"]'
    );

    if (existingScript) {
      // Script already loaded, wait for grecaptcha to be ready
      const checkRecaptchaReady = () => {
        if (window.grecaptcha && window.grecaptcha.enterprise) {
          window.grecaptcha.enterprise.ready(() => {
            setIsReady(true);
            setScriptLoaded(true);
          });
        } else {
          setTimeout(checkRecaptchaReady, 100);
        }
      };
      checkRecaptchaReady();
      return;
    }

    // Load the reCAPTCHA Enterprise script dynamically
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${recaptchaSiteKey}`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      setScriptLoaded(true);
      // Wait for grecaptcha.enterprise.ready
      const checkReady = () => {
        if (window.grecaptcha && window.grecaptcha.enterprise) {
          window.grecaptcha.enterprise.ready(() => {
            setIsReady(true);
          });
        } else {
          setTimeout(checkReady, 100);
        }
      };
      checkReady();
    };

    script.onerror = () => {
      console.error('Failed to load reCAPTCHA Enterprise script');
      setIsReady(false);
    };

    document.head.appendChild(script);

    // Cleanup function
    return () => {
      // Note: We don't remove the script to avoid breaking other components
      // that might be using it
    };
  }, [recaptchaSiteKey, recaptchaEnabled]);

  /**
   * Execute reCAPTCHA Enterprise and return token
   * @param {string} action - The action name for reCAPTCHA
   * @returns {Promise<string|null>} - The reCAPTCHA token or null
   */
  const executeRecaptcha = useCallback(
    async (action) => {
      if (!recaptchaEnabled || !recaptchaSiteKey) {
        console.log('reCAPTCHA Enterprise is disabled');
        return null;
      }

      if (!isReady || !scriptLoaded) {
        console.warn('reCAPTCHA Enterprise is not ready yet');
        return null;
      }

      if (!window.grecaptcha || !window.grecaptcha.enterprise) {
        console.error('reCAPTCHA Enterprise not available');
        return null;
      }

      try {
        const token = await window.grecaptcha.enterprise.execute(
          recaptchaSiteKey,
          { action }
        );
        return token;
      } catch (error) {
        console.error('Error executing reCAPTCHA Enterprise:', error);
        return null;
      }
    },
    [recaptchaSiteKey, recaptchaEnabled, isReady, scriptLoaded]
  );

  return { isReady, executeRecaptcha };
};
